/**
 * Decision view model (docs/ux/project-tab-spec.md §1.6, §3.6): one shape for the decision view,
 * Inbox rows and the Lanes asks panel, built either from the sealed `decision` entity or — for
 * asks that have none (CLI questions, older runs) — transiently from the question event, with the
 * thread disabled ("Reply in conversation"). Pure: no app imports beyond types.
 */

import type { OptionMedia, OptionMix } from '@viko/protocol' // visual-review
import {
  type DecisionCategory,
  type DecisionDelivery,
  type DecisionRecord,
  type DecisionStatus,
  decisionAnswerKind,
  describeBoardAnswer, // visual-review
  isRiskyDecisionCategory,
} from '@viko/protocol'
import { waitedText } from './lanes-model.js'
import type { InboxItem } from './types.js'

export const CATEGORY_LABEL: Record<DecisionCategory, string> = {
  taste: 'Taste',
  product: 'Product',
  irreversible: 'Irreversible',
  money: 'Money',
  secrets: 'Secrets',
  security: 'Security',
  release: 'Release',
  other: 'Decision',
}

export interface OptionView {
  id: string
  /** Quick-pick key (1–9). */
  key: number
  label: string
  description?: string
  rationale?: string
  pros: string[]
  cons: string[]
  reversible?: boolean
  recommended: boolean
  /** visual-review: images / HTML boards of a design option. */
  media: OptionMedia[]
}

export interface EvidenceView {
  kind: 'artifact' | 'diff' | 'test' | 'commit' | 'transcript' | 'link'
  ref: string
  label: string
  excerpt?: string
  status?: 'pass' | 'fail' | 'running'
  runId?: string
  seq?: number
  artifact?: DecisionRecord['evidence'][number]['artifact']
  mime?: string
  /** The agent flagged it as directly relevant (shown large in the focus area) or as context. */
  relevance?: 'direct' | 'context'
}

export interface ThreadView {
  id: string
  at: number
  author: 'user' | 'agent' | 'triage' | 'system'
  text: string
  attachments: NonNullable<DecisionRecord['thread'][number]['attachments']>
  revisedOptions: boolean
  delivery?: DecisionDelivery
}

export interface DecisionView {
  /** Inspector key: `decision:<id>` (entity) or `ask:<hub item id>` (legacy). */
  key: string
  id: string
  source: 'entity' | 'legacy'
  title: string
  question: string
  why?: string
  category: DecisionCategory
  categoryLabel: string
  risky: boolean
  simple: boolean
  /** Offer 1–9 quick-pick in rows, the asks panel and pushes. */
  quickPick: boolean
  blocking: boolean
  unblocks: number
  leverage: string[]
  skipText: string
  status: DecisionStatus
  open: boolean
  options: OptionView[]
  suggested?: OptionView
  revision: { n: number; at?: number; previous?: OptionView[] }
  evidence: EvidenceView[]
  /** 2–3 one-line points on how it got here (shown under the question). */
  keyPoints: { label: string; text: string }[]
  thread: ThreadView[]
  threadEnabled: boolean
  /** Free-text answers allowed. */
  freeform: boolean
  /** Legacy event with several questions: answered with the classic card. */
  multiQuestion: boolean
  answerText?: string
  assumptionText?: string
  assumption?: string
  projectId?: string
  runId?: string
  seq?: number
  questionId?: string
  machineId?: string
  taskId?: string
  workstreamId?: string
  agent?: 'claude' | 'codex'
  createdAt: number
  snoozedUntil?: number
  decision?: DecisionRecord
  item?: InboxItem
  // ---- visual-review: a design-option board (options with media, areas to mix, sign-off gate) ----
  board?: {
    areas: string[]
    /** Tasks the answer is written into. */
    fanOut: number
    gate?: { state: 'waiting' | 'signed-off' | 'released'; tasks: number }
  }
  // ---- end visual-review ----
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`

function leverageOf(blocking: boolean, unblocks: number, createdAt: number, now: number) {
  const out: string[] = []
  if (unblocks > 0) out.push(`Unblocks ${plural(unblocks, 'task')}`)
  else if (blocking) out.push('Blocks a run')
  else out.push('Blocks nothing')
  out.push(waitedText(now - createdAt))
  return out
}

function skipText(blocking: boolean, assumption?: string) {
  if (blocking) return 'This run stays paused until you answer; other lanes keep going.'
  if (assumption) return `The agent keeps going with: ${assumption}`
  return 'Nothing is blocked; the agent keeps working.'
}

const toOption = (o: DecisionRecord['options'][number], i: number): OptionView => ({
  id: o.id,
  key: i + 1,
  label: o.label,
  ...(o.description ? { description: o.description } : {}),
  ...(o.rationale ? { rationale: o.rationale } : {}),
  pros: o.tradeoffs.pro,
  cons: o.tradeoffs.con,
  reversible: o.reversible,
  recommended: o.recommended,
  media: o.media ?? [],
})

export function viewFromDecision(d: DecisionRecord, now: number, item?: InboxItem): DecisionView {
  const options = d.options.map(toOption)
  const risky = isRiskyDecisionCategory(d.category)
  const blocking = item?.blocking ?? d.blocking
  const unblocks = d.unblocks ?? item?.unblocks ?? 0
  const picked = d.answer?.optionId ? options.find((o) => o.id === d.answer?.optionId) : undefined
  const answerText = !d.answer
    ? undefined
    : d.answer.mode === 'pick'
      ? d.optionSet || d.answer.mix?.length || d.answer.comments
        ? // visual-review: "C (Pill) for buttons, D (Sharp) for toolbars. Comments: …"
          `You chose ${describeBoardAnswer(d.options, d.answer)}`
        : `You picked ${picked?.label ?? 'an option'}`
      : d.answer.mode === 'freeform'
        ? `You answered: ${d.answer.text ?? ''}`
        : `You asked the agent to decide${d.answer.guidance ? `: ${d.answer.guidance}` : ''}`
  const r = d.resolution
  const assumptionText = r?.assumption
    ? `Assumed ${r.label ?? options.find((o) => o.id === r.optionId)?.label ?? 'an option'}: ${r.assumption}`
    : undefined
  return {
    key: `decision:${d.id}`,
    id: d.id,
    source: 'entity',
    title: d.title || shortTitle(d.question),
    question: d.question,
    ...(d.why ? { why: d.why } : {}),
    category: d.category,
    categoryLabel: CATEGORY_LABEL[d.category],
    risky,
    simple: d.simple,
    quickPick: d.simple && !risky && options.length > 0 && options.length <= 9,
    blocking,
    unblocks,
    leverage: leverageOf(blocking, unblocks, d.createdAt, now),
    skipText: skipText(blocking, d.assumption),
    status: d.status,
    open: d.status === 'open',
    options,
    ...(options.find((o) => o.recommended)
      ? { suggested: options.find((o) => o.recommended) }
      : {}),
    revision: {
      n: d.optionsRevision,
      ...(d.revisedAt ? { at: d.revisedAt } : {}),
      ...(d.previousOptions ? { previous: d.previousOptions.map(toOption) } : {}),
    },
    evidence: d.evidence.map((e) => ({ ...e })),
    keyPoints: (d.keyPoints ?? []).slice(0, 3),
    thread: d.thread.map((m) => ({
      id: m.id,
      at: m.at,
      author: m.author,
      text: m.text,
      attachments: m.attachments ?? [],
      revisedOptions: !!m.revisedOptions,
      ...(m.delivery ? { delivery: m.delivery } : {}),
    })),
    threadEnabled: true,
    freeform: true,
    multiQuestion: false,
    ...(answerText ? { answerText } : {}),
    ...(assumptionText ? { assumptionText } : {}),
    ...(d.assumption ? { assumption: d.assumption } : {}),
    projectId: d.projectId,
    ...(d.runId ? { runId: d.runId } : {}),
    ...(d.seq ? { seq: d.seq } : {}),
    ...(d.questionId ? { questionId: d.questionId } : {}),
    ...(d.machineId ? { machineId: d.machineId } : {}),
    ...(d.taskId ? { taskId: d.taskId } : {}),
    ...(d.workstreamId ? { workstreamId: d.workstreamId } : {}),
    ...(d.agent ? { agent: d.agent } : {}),
    createdAt: d.createdAt,
    ...(d.snoozedUntil ? { snoozedUntil: d.snoozedUntil } : {}),
    decision: d,
    ...(item ? { item } : {}),
    // ---- visual-review ----
    ...(d.optionSet
      ? {
          board: {
            areas: d.optionSet.areas,
            fanOut: d.optionSet.fanOut.length,
            ...(d.optionSet.gate
              ? { gate: { state: d.optionSet.gate.state, tasks: d.optionSet.gate.taskIds.length } }
              : {}),
          },
        }
      : {}),
    // ---- end visual-review ----
  }
}

const SUGGESTED = /\s*\((recommended|suggested)\)\s*/i

/** §3.6: a transient view from the question event when no decision entity exists. */
export function viewFromLegacy(item: InboxItem, now: number): DecisionView {
  const e = item.event?.kind === 'question' ? item.event : undefined
  const q = e?.questions[0]
  const options: OptionView[] = (q?.options ?? []).map((o, i) => ({
    id: `q0:${i}`,
    key: i + 1,
    label: o.label.replace(SUGGESTED, '').trim() || o.label,
    ...(o.description ? { description: o.description } : {}),
    pros: [],
    cons: [],
    recommended: SUGGESTED.test(o.label),
    media: [],
  }))
  const multi = (e?.questions.length ?? 0) > 1
  const blocking = item.blocking
  const unblocks = item.unblocks ?? 0
  const multiSelect = !!q?.multiSelect
  return {
    key: `ask:${item.itemId ?? item.id}`,
    id: item.itemId ?? item.id,
    source: 'legacy',
    title: item.locked ? item.title : q?.header?.trim() || shortTitle(q?.question ?? item.title),
    question: item.locked ? '' : (q?.question ?? item.title),
    category: 'other',
    categoryLabel: CATEGORY_LABEL.other,
    risky: false,
    simple: !multi && !multiSelect && options.length > 0 && options.length <= 4,
    quickPick: !item.locked && !multi && !multiSelect && options.length > 0 && options.length <= 9,
    blocking,
    unblocks,
    leverage: leverageOf(blocking, unblocks, item.createdAt, now),
    skipText: skipText(blocking),
    status: 'open',
    open: true,
    options: item.locked ? [] : options,
    ...(options.find((o) => o.recommended)
      ? { suggested: options.find((o) => o.recommended) }
      : {}),
    revision: { n: 0 },
    evidence: [],
    keyPoints: [],
    thread: [],
    threadEnabled: false,
    freeform: q?.allowOther !== false,
    multiQuestion: multi,
    ...(item.runId ? { runId: item.runId } : {}),
    ...(item.seq ? { seq: item.seq } : {}),
    questionId: item.id,
    ...(item.machineId ? { machineId: item.machineId } : {}),
    ...(item.workstreamId ? { workstreamId: item.workstreamId } : {}),
    ...(item.agent ? { agent: item.agent } : {}),
    createdAt: item.createdAt,
    item,
  }
}

/** The first sentence (or 80 chars) of a question, for rows. */
export function shortTitle(text: string): string {
  const t = text.trim().replace(/\s+/g, ' ')
  const first = /^(.{8,}?[?.!])(\s|$)/.exec(t)?.[1] ?? t
  return first.length > 80 ? `${first.slice(0, 79).trimEnd()}…` : first
}

export function deliveryText(d: DecisionDelivery | undefined): string {
  if (!d) return 'Sending…'
  switch (d.status) {
    case 'delivered':
      return 'Delivered'
    case 'resumed':
      return 'Delivered · agent resumed'
    case 'queued':
    case 'sent':
      return 'Queued for the agent'
    case 'triage':
      return 'The run had ended · handed to the triage agent'
    case 'failed':
      return `Not delivered${d.note ? `: ${d.note}` : ''}`
  }
}

/** Revised options vs the previous set (the diff toggle). */
export function optionDiff(v: Pick<DecisionView, 'options' | 'revision'>) {
  const prev = v.revision.previous ?? []
  const byLabel = new Map(prev.map((o) => [o.label, o]))
  const now = new Set(v.options.map((o) => o.label))
  return {
    added: v.options.filter((o) => !byLabel.has(o.label)).map((o) => o.label),
    removed: prev.filter((o) => !now.has(o.label)).map((o) => o.label),
    changed: v.options
      .filter((o) => {
        const p = byLabel.get(o.label)
        return (
          !!p &&
          ((p.rationale ?? '') !== (o.rationale ?? '') ||
            p.recommended !== o.recommended ||
            p.pros.join('\n') !== o.pros.join('\n') ||
            p.cons.join('\n') !== o.cons.join('\n'))
        )
      })
      .map((o) => o.label),
  }
}

export type AnswerInput =
  /** `note`: the user's optional note with the pick (Claude "add notes" parity). */
  | {
      mode: 'pick'
      optionId: string
      note?: string
      /** visual-review: "C for buttons, D for toolbars" and per-option comments. */
      mix?: OptionMix
      comments?: Record<string, string>
    }
  | { mode: 'freeform'; text: string }
  | { mode: 'delegate'; guidance?: string }

/** The signed command for an entity decision (risky categories → the step-up kind). */
export function answerCommand(v: DecisionView, a: AnswerInput, o: { callId?: string } = {}) {
  if (v.source !== 'entity' || !v.projectId) throw new Error('not a decision entity')
  const guidance = a.mode === 'delegate' ? a.guidance?.trim() : undefined
  return {
    kind: decisionAnswerKind(v.category),
    payload: {
      decisionId: v.id,
      projectId: v.projectId,
      mode: a.mode,
      ...(a.mode === 'pick' ? { optionId: a.optionId } : {}),
      ...(a.mode === 'pick' && a.note?.trim() ? { text: a.note.trim() } : {}),
      ...(a.mode === 'pick' && a.mix?.length ? { mix: a.mix } : {}), // visual-review
      ...(a.mode === 'pick' && a.comments && Object.keys(a.comments).length
        ? { comments: a.comments }
        : {}),
      ...(a.mode === 'freeform' ? { text: a.text.trim() } : {}),
      ...(guidance ? { guidance } : {}),
      ...(o.callId ? { callId: o.callId } : {}), // viko-call: "via call" in History
    },
  }
}

/** `run.answer` answers for a legacy question (first question only; others use the card). */
export function legacyAnswers(v: DecisionView, a: AnswerInput): Record<string, string[]> {
  const q = v.item?.event?.kind === 'question' ? v.item.event.questions[0] : undefined
  const qid = q?.id ?? 'q0'
  if (a.mode === 'pick') {
    const i = v.options.findIndex((o) => o.id === a.optionId)
    const raw = q?.options[i]?.label ?? v.options[i]?.label ?? ''
    const note = a.note?.trim()
    return { [qid]: [note ? `${raw}. Note: ${note}` : raw] }
  }
  if (a.mode === 'freeform') return { [qid]: [a.text.trim()] }
  const g = a.guidance?.trim()
  return { [qid]: [`You decide${g ? ` (${g})` : ''}. Tell me which one you picked.`] }
}
